import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleDetail } from '@/mocks/articleDetails';
import { useJsonLd } from '@/hooks/useJsonLd';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleDetail(id) : undefined;

  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.heroImageUrl,
    "datePublished": article.date,
    "author": {
      "@type": "Organization",
      "name": article.source
    },
    "publisher": {
      "@type": "Organization",
      "name": "51吃瓜黑料",
      "url": "http://ahyuntong.cn/"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `http://ahyuntong.cn/article/${id}`
    }
  } : null;

  const breadcrumbSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "http://ahyuntong.cn/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.category,
        "item": `http://ahyuntong.cn/category/${article.categorySlug}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.title
      }
    ]
  } : null;

  useJsonLd(articleSchema);
  useJsonLd(breadcrumbSchema);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <p className="text-foreground-400 text-lg mb-4">文章未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.heroImageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
          <div className="relative z-10 w-full h-full flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
                  <Link to="/" className="text-background-50/60 hover:text-background-50 transition-colors">首页</Link>
                  <span className="text-background-50/40">/</span>
                  <Link to={`/category/${article.categorySlug}`} className="text-background-50/60 hover:text-background-50 transition-colors">{article.category}</Link>
                  <span className="text-background-50/40">/</span>
                  <span className="text-background-50/80 text-xs md:text-sm">{article.title.slice(0, 20)}...</span>
                </nav>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight max-w-3xl">
                  {article.titleLine1}
                  <br />
                  {article.titleLine2}
                  <br />
                  {article.titleLine3}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-5">
                  <span className="px-2.5 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">{article.tag}</span>
                  <span className="text-background-50/60 text-xs">来源：{article.source}</span>
                  <span className="text-background-50/60 text-xs">{article.date}</span>
                  <span className="text-background-50/60 text-xs">{article.views} 阅读</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                {/* Left - Sticky Image */}
                <div className="w-full lg:w-1/2">
                  <div className="sticky top-24">
                    <div className="rounded-2xl overflow-hidden bg-background-100">
                      <img
                        src={article.heroImageUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full object-cover object-top"
                      />
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer">
                        <i className="ri-heart-line"></i> 收藏
                      </button>
                      <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer">
                        <i className="ri-share-forward-line"></i> 分享
                      </button>
                      <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer">
                        <i className="ri-bookmark-line"></i> 收藏夹
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right - Article Body */}
                <div className="w-full lg:w-1/2">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-6">
                    {article.tag}
                  </div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 leading-tight">
                    {article.title}
                  </h2>
                  <div
                    className="prose prose-sm md:prose-base max-w-none text-foreground-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.contentHtml }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 items-center">
                <div className="w-full lg:w-[40%]">
                  <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-accent-100">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20toned%20abstract%20illustration%20of%20connected%20news%20and%20articles%20network%2C%20soft%20amber%20and%20cream%20colors%2C%20minimalist%20style%2C%20editorial%20aesthetic%2C%20clean%20simple%20background&width=600&height=600&seq=related-reading&orientation=squarish"
                      alt="关联阅读"
                      title="你可能还想吃这些瓜"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-[60%]">
                  <p className="text-foreground-400 font-label text-sm mb-4">（关联阅读）</p>
                  <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-8 leading-tight">
                    你可能还想
                    <br />
                    <span className="text-foreground-400">吃这些瓜</span>
                  </h2>
                  <div className="flex flex-col gap-5">
                    {article.relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to="/category/all"
                        className="group block"
                      >
                        <p className="text-foreground-600 text-sm leading-relaxed mb-1 group-hover:text-primary-600 transition-colors">
                          {rel.excerpt}
                        </p>
                        <div className="flex items-center gap-3">
                          <span className="text-foreground-400 text-xs">{rel.date}</span>
                          <span className="text-foreground-300">—</span>
                          <span className="font-heading font-semibold text-foreground-950 text-sm group-hover:text-primary-600 transition-colors">
                            {rel.title.slice(0, 25)}...
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="flex flex-col lg:flex-row">
            <div className="w-full lg:w-1/2 relative h-[350px] md:h-[500px] overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20toned%20abstract%20digital%20art%20of%20community%20group%20chat%20concept%2C%20amber%20and%20cream%20gradients%2C%20minimalist%20design%2C%20editorial%20style%2C%20clean%20composition&width=800&height=1000&seq=article-cta-left&orientation=portrait"
                alt="加入瓜友群"
                title="加入瓜友群 一起吃瓜"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                  加入瓜友群
                  <br />
                  一起吃瓜
                  <br />
                  爆料不停
                </p>
              </div>
            </div>
            <div className="w-full lg:w-1/2 bg-background-50 flex items-center justify-center py-14 md:py-20 px-8 md:px-16">
              <div className="max-w-md text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight">
                  爆料投稿
                  <br />
                  人人都是狗仔
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                  如果你有独家爆料线索，欢迎向我们投稿。每一条真实爆料都将获得平台流量扶持，让你的声音被千万瓜友听见。
                </p>
                <Link
                  to="/about#contact"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors duration-200"
                >
                  立即投稿
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}